/**
 * Affiliate Form Component
 * Create or edit an affiliate
 */

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import {
  createAffiliate,
  updateAffiliate,
} from "../../services/affiliates.service";
import Button from "../common/Button";
import type { Affiliate, AffiliateFormData } from "../../types/partner.types";
import type { UUID } from "../../types/common.types";

const affiliateSchema = z.object({
  affiliate_number: z
    .string()
    .regex(/^[A-Z0-9]{6}$/, "Must be exactly 6 alphanumeric characters")
    .optional()
    .or(z.literal("")),
  name: z.string().min(1, "Affiliate name is required"),
  phone: z.string().optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
  website: z.string().url("Invalid URL").optional().or(z.literal("")),
  notes: z.string().optional(),
  is_active: z.boolean().optional(),
});

type FormData = z.infer<typeof affiliateSchema>;

interface AffiliateFormProps {
  partnerId: UUID;
  affiliate?: Affiliate;
  onSuccess?: (affiliate: Affiliate) => void;
  onCancel?: () => void;
}

const AffiliateForm: React.FC<AffiliateFormProps> = ({
  partnerId,
  affiliate,
  onSuccess,
  onCancel,
}) => {
  const isEditing = !!affiliate;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(affiliateSchema),
    defaultValues: {
      affiliate_number: affiliate?.affiliate_number || "",
      name: affiliate?.name || "",
      phone: affiliate?.phone || "",
      address_line1: affiliate?.address_line1 || "",
      address_line2: affiliate?.address_line2 || "",
      city: affiliate?.city || "",
      state: affiliate?.state || "",
      postal_code: affiliate?.postal_code || "",
      country: affiliate?.country || "US",
      website: affiliate?.website || "",
      notes: affiliate?.notes || "",
      is_active: affiliate?.is_active ?? true,
    },
  });

  const affiliateNumber = watch("affiliate_number");

  const createMutation = useMutation({
    mutationFn: (data: AffiliateFormData) => createAffiliate(data),
    onSuccess: (result) => onSuccess?.(result),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<AffiliateFormData>) =>
      updateAffiliate(affiliate!.id, data),
    onSuccess: (result) => onSuccess?.(result),
  });

  const onSubmit = async (data: FormData) => {
    // Clean empty strings to undefined
    const cleanedData: AffiliateFormData = {
      partner_id: partnerId,
      affiliate_number: data.affiliate_number?.toUpperCase() || undefined,
      name: data.name,
      phone: data.phone || undefined,
      address_line1: data.address_line1 || undefined,
      address_line2: data.address_line2 || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      postal_code: data.postal_code || undefined,
      country: data.country || undefined,
      website: data.website || undefined,
      notes: data.notes || undefined,
      is_active: data.is_active,
    };

    if (isEditing) {
      // Remove affiliate_number from updates (can't be changed)
      const { affiliate_number, ...updateData } = cleanedData;
      updateMutation.mutate(updateData);
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {(error as Error).message || "An error occurred"}
        </div>
      )}

      {/* Basic Info */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Affiliate Number
              {!isEditing && (
                <span className="text-gray-400 font-normal ml-1">
                  (auto-generated if blank)
                </span>
              )}
            </label>
            <input
              type="text"
              {...register("affiliate_number")}
              disabled={isEditing}
              maxLength={6}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase font-mono ${
                isEditing ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
              placeholder="A1B2C3"
              onChange={(e) => {
                e.target.value = e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "");
              }}
            />
            {errors.affiliate_number && (
              <p className="mt-1 text-sm text-red-600">
                {errors.affiliate_number.message}
              </p>
            )}
            {!isEditing &&
              affiliateNumber &&
              affiliateNumber.length > 0 &&
              affiliateNumber.length < 6 && (
                <p className="mt-1 text-sm text-yellow-600">
                  Enter exactly 6 characters or leave blank for auto-generation
                </p>
              )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Name *
            </label>
            <input
              type="text"
              {...register("name")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Store Name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              {...register("phone")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              {...register("website")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="https://example.com"
            />
            {errors.website && (
              <p className="mt-1 text-sm text-red-600">
                {errors.website.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Address</h4>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 1
          </label>
          <input
            type="text"
            {...register("address_line1")}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="123 Main Street"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2
          </label>
          <input
            type="text"
            {...register("address_line2")}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Suite 100"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="col-span-2 md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              {...register("city")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Houston"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <input
              type="text"
              {...register("state")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="TX"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postal Code
            </label>
            <input
              type="text"
              {...register("postal_code")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="77001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <input
              type="text"
              {...register("country")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="US"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          {...register("notes")}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Additional notes about this affiliate..."
        />
      </div>

      {/* Status */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_active"
          {...register("is_active")}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="is_active" className="text-sm text-gray-700">
          Affiliate is active
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={isPending || isSubmitting}
        >
          {isPending
            ? "Saving..."
            : isEditing
              ? "Update Affiliate"
              : "Create Affiliate"}
        </Button>
      </div>
    </form>
  );
};

export default AffiliateForm;
